import { useState, useMemo } from 'react'
import {Copy, MessageCircleDashed as MessageCircle, ChevronDown, ChevronUp, ShoppingCart, AlertTriangle, Sparkles} from 'lucide-react'
import toast from 'react-hot-toast'
import { gerarProtocoloPrioritario, gerarProtocoloPrioritarioPorPrioridades } from '@/utils/prescricao'
import { copiarTexto as copiarParaArea } from '@/utils/clipboard'

interface OpcoesCompraProps {
  lead: any
}

// ══════════════════════════════════════════
// TABELA DE PRECOS E PV — BRASIL
// ══════════════════════════════════════════

interface ProdutoInfo {
  nome: string
  tamanho: string
  preco: number
  pv: number
}

const TABELA_PRECOS_BR: Record<string, ProdutoInfo> = {
  'Lavanda': { nome: 'Lavanda', tamanho: '5ml', preco: 77.50, pv: 10 },
  'Hortelã-Pimenta': { nome: 'Hortelã-Pimenta', tamanho: '5ml', preco: 68.75, pv: 9 },
  'Limão Siciliano': { nome: 'Limão Siciliano', tamanho: '5ml', preco: 36.25, pv: 4 },
  'Tangerina': { nome: 'Tangerina', tamanho: '5ml', preco: 52.50, pv: 7 },
  'Copaíba': { nome: 'Copaíba', tamanho: '5ml', preco: 120.00, pv: 16 },
  'Olíbano': { nome: 'Olíbano', tamanho: '5ml', preco: 202.50, pv: 27 },
  'Melaleuca': { nome: 'Melaleuca', tamanho: '15ml', preco: 157.50, pv: 21 },
  'Mix de Proteção': { nome: 'Mix de Proteção (On Guard)', tamanho: '5ml', preco: 105.00, pv: 14 },
  'Mix Calmante': { nome: 'Mix Calmante (Deep Blue)', tamanho: '5ml', preco: 216.00, pv: 36 },
  'Mix para Aterramento': { nome: 'Mix para Aterramento (Balance)', tamanho: '15ml', preco: 157.50, pv: 21 },
  'Mix Respiratório': { nome: 'Mix Respiratório (Breathe)', tamanho: '15ml', preco: 172.50, pv: 23 },
  'Mix Digestivo': { nome: 'Mix Digestivo (Zengest)', tamanho: '5ml', preco: 105.00, pv: 11 },
  'Mix Metabólico': { nome: 'Mix Metabólico (Meta PWR)', tamanho: '15ml', preco: 223.75, pv: 27 },
  'Mix Mensal Feminino': { nome: 'Mix Mensal Feminino (ClaryCalm)', tamanho: 'Roll-on 10ml', preco: 218.75, pv: 28 },
}

// ══════════════════════════════════════════
// TABELA DE PRECOS E PV — PORTUGAL
// ══════════════════════════════════════════

const TABELA_PRECOS_PT: Record<string, ProdutoInfo> = {
  'Lavanda': { nome: 'Lavanda', tamanho: '15ml', preco: 38.24, pv: 36 },
  'Hortelã-Pimenta': { nome: 'Hortelã-Pimenta', tamanho: '15ml', preco: 31.78, pv: 29.50 },
  'Limão Siciliano': { nome: 'Limão Siciliano', tamanho: '15ml', preco: 18.61, pv: 17.50 },
  'Tangerina': { nome: 'Tangerina', tamanho: '15ml', preco: 19.64, pv: 18.50 },
  'Copaíba': { nome: 'Copaíba', tamanho: '15ml', preco: 46.78, pv: 43.50 },
  'Olíbano': { nome: 'Olíbano', tamanho: '5ml', preco: 41.50, pv: 39 },
  'Melaleuca': { nome: 'Melaleuca', tamanho: '15ml', preco: 30.23, pv: 28 },
  'Mix de Proteção': { nome: 'Mix de Proteção (On Guard)', tamanho: '15ml', preco: 46.78, pv: 43.50 },
  'Mix Calmante': { nome: 'Mix Calmante (Deep Blue)', tamanho: '5ml', preco: 45.47, pv: 42.50 },
  'Mix para Aterramento': { nome: 'Mix para Aterramento (Balance)', tamanho: '15ml', preco: 31.27, pv: 29 },
  'Mix Respiratório': { nome: 'Mix Respiratório (Air)', tamanho: '15ml', preco: 36.95, pv: 34.50 },
  'Mix Digestivo': { nome: 'Mix Digestivo (Zengest)', tamanho: '15ml', preco: 44.44, pv: 41.50 },
  'Mix Metabólico': { nome: 'Mix Metabólico (Meta PWR)', tamanho: '15ml', preco: 34.88, pv: 32.50 },
  'Mix Mensal Feminino': { nome: 'Mix Mensal Feminino (ClaryCalm)', tamanho: '10ml', preco: 43.15, pv: 40.50 },
}

// Aliases para nomes que vêm do diagnóstico
const ALIAS_OLEOS: Record<string, string> = {
  'Mix para Estabilidade': 'Mix para Aterramento',
  'Mix Imunidade': 'Mix de Proteção',
  'Mix para Tensão e Dores': 'Mix Calmante',
}

// Aliases para nomes que vêm do protocolo prioritário (com nome comercial)
const ALIAS_PRIORITARIO: Record<string, string> = {
  'Mix para Aterramento - Balance': 'Mix para Aterramento',
  'Mix de Proteção - On Guard': 'Mix de Proteção',
  'Mix Respiratório - Breathe': 'Mix Respiratório',
  'Mix Calmante - Deep Blue': 'Mix Calmante',
  'Mix Digestivo - Zengest': 'Mix Digestivo',
  'Mix Metabólico - Meta PWR': 'Mix Metabólico',
  'Mix Mensal Feminino - ClaryCalm': 'Mix Mensal Feminino',
}

// Brasil
const KIT_BRASIL_PRECO = 720
const KIT_BRASIL_PV = 120
const OLEO_COCO_PRECO_BR = 106.25
const OLEO_COCO_PV_BR = 11
const FRETE_BR = 32
const TAXA_ADESAO_BR = 48
const PV_MINIMO_SEM_TAXA = 100

// Portugal
const KIT_PT_PRECO = 176.49
const KIT_PT_PV = 147.50
const OLEO_COCO_PRECO_PT = 19.90
const OLEO_COCO_PV_PT = 19
const FRETE_DPD = 8.61
const FRETE_DHL = 14.76
const TAXA_INSCRICAO_PT = 20

// Óleos incluídos no Kit Essenciais Familiar (Portugal)
const OLEOS_KIT_PT = [
  'Lavanda', 'Limão Siciliano', 'Hortelã-Pimenta', 'Melaleuca',
  'Olíbano', 'Mix Calmante', 'Mix Respiratório', 'Mix Digestivo', 'Mix de Proteção'
]
// Orégano está no kit PT mas não na tabela de óleos individuais do diagnóstico

// Óleos adicionais que podem ser acrescentados ao Kit PT se indicados
const OLEOS_ADICIONAIS_KIT_PT = [
  'Mix Metabólico', 'Mix Mensal Feminino', 'Mix para Aterramento', 'Tangerina', 'Copaíba'
]

// ══════════════════════════════════════════
// FUNCOES AUXILIARES
// ══════════════════════════════════════════

function resolverNomeOleo(nome: string): string {
  return ALIAS_OLEOS[nome] || nome
}

function resolverNomePrioritario(nome: string): string {
  return ALIAS_PRIORITARIO[nome] || ALIAS_OLEOS[nome] || nome
}

function getProdutoInfo(nomeResolvido: string, isPortugal: boolean): ProdutoInfo | null {
  const tabela = isPortugal ? TABELA_PRECOS_PT : TABELA_PRECOS_BR
  return tabela[nomeResolvido] || null
}

function formatarPreco(valor: number, isPortugal: boolean): string {
  if (isPortugal) {
    return valor.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function moeda(isPortugal: boolean): string {
  return isPortugal ? '\u20ac' : 'R$'
}

function calcularParcelamentoBR(total: number): string {
  // Parcelamento sempre calculado sobre o total com frete
  // < R$300: à vista | R$300 - R$599,99: 3x | >= R$600: 3x e 6x
  if (total >= 600) {
    const p3 = total / 3
    const p6 = total / 6
    return `- Parcelado em 3x de R$ ${formatarPreco(p3, false)} no cartão\n- Parcelado em 6x de R$ ${formatarPreco(p6, false)} no cartão`
  }
  if (total >= 300) {
    const parcela = total / 3
    return `- Parcelado em 3x de R$ ${formatarPreco(parcela, false)} no cartão`
  }
  return ''
}

function formatarNumeroWhatsApp(whatsapp: string): string {
  const apenasDigitos = (whatsapp || '').replace(/\D/g, '')
  if (!apenasDigitos) return ''
  if (apenasDigitos.startsWith('55') && apenasDigitos.length >= 12) return apenasDigitos
  if (apenasDigitos.length >= 10 && apenasDigitos.length <= 11) return `55${apenasDigitos}`
  return apenasDigitos
}

// ══════════════════════════════════════════
// GERACAO DAS OPCOES — BRASIL
// ══════════════════════════════════════════

function gerarOpcao1BR(lead: any): string {
  const oleosIndicados: string[] = (lead.oleos_indicados || []).map(resolverNomeOleo)
  const temMetaPWR = oleosIndicados.includes('Mix Metabólico')
  const temClaryCalm = oleosIndicados.includes('Mix Mensal Feminino')

  let somaProdutos = KIT_BRASIL_PRECO + OLEO_COCO_PRECO_BR
  if (temMetaPWR) somaProdutos += 223.75
  if (temClaryCalm) somaProdutos += 218.75

  const total = somaProdutos + FRETE_BR
  const parcelamento = calcularParcelamentoBR(total)

  let texto = `*OPÇÃO 1 — Kit Brasil Completo*\n\n`
  texto += `Este kit reúne os 12 óleos essenciais mais vendidos e queridos da dōTERRA, todos já incluídos no protocolo indicado para você:\n\n`
  texto += `- Lavanda\n`
  texto += `- Hortelã-Pimenta\n`
  texto += `- Limão Siciliano\n`
  texto += `- Tangerina\n`
  texto += `- Copaíba\n`
  texto += `- Olíbano\n`
  texto += `- Melaleuca (Mix Melaleuca)\n`
  texto += `- Mix de Proteção (On Guard)\n`
  texto += `- Mix Calmante (Deep Blue)\n`
  texto += `- Mix para Aterramento (Balance)\n`
  texto += `- Mix Respiratório (Breathe)\n`
  texto += `- Mix Digestivo (Zengest)\n`
  if (temMetaPWR) texto += `- Mix Metabólico (Meta PWR) 15ml\n`
  if (temClaryCalm) texto += `- Mix Mensal Feminino (ClaryCalm) 10ml\n`
  texto += `- Óleo de Coco Fracionado 115ml (essencial para diluição e uso tópico)\n`

  texto += `\n*Investimento:*\n`
  texto += `- Produtos: R$ ${formatarPreco(somaProdutos, false)}\n`
  texto += `- Frete: R$ ${formatarPreco(FRETE_BR, false)}\n`
  texto += `- *Total: R$ ${formatarPreco(total, false)}*\n`
  if (parcelamento) texto += `${parcelamento}\n`
  texto += `- Ou à vista no Pix / boleto`

  return texto
}

function gerarOpcao2BR(lead: any): string {
  const oleosIndicados: string[] = lead.oleos_indicados || []
  const produtosLista: { info: ProdutoInfo }[] = []
  let somaProdutos = 0
  let totalPV = 0

  const oleosJaAdicionados = new Set<string>()
  oleosIndicados.forEach((oleo: string) => {
    const nomeResolvido = resolverNomeOleo(oleo)
    if (oleosJaAdicionados.has(nomeResolvido)) return
    const info = getProdutoInfo(nomeResolvido, false)
    if (info) {
      oleosJaAdicionados.add(nomeResolvido)
      produtosLista.push({ info })
      somaProdutos += info.preco
      totalPV += info.pv
    }
  })

  somaProdutos += OLEO_COCO_PRECO_BR
  totalPV += OLEO_COCO_PV_BR

  const precisaTaxa = totalPV < PV_MINIMO_SEM_TAXA
  let total = somaProdutos + FRETE_BR
  if (precisaTaxa) total += TAXA_ADESAO_BR

  const parcelamento = calcularParcelamentoBR(total)

  let texto = `*OPÇÃO 2 — Todos os Óleos do Seu Protocolo (Comprando avulso sem kit)*\n\n`
  produtosLista.forEach(({ info }) => {
    texto += `- ${info.nome} ${info.tamanho} -> R$ ${formatarPreco(info.preco, false)}\n`
  })
  texto += `- Óleo de Coco Fracionado 115ml -> R$ ${formatarPreco(OLEO_COCO_PRECO_BR, false)} (essencial para diluição e uso tópico)\n`
  if (precisaTaxa) texto += `- Taxa de adesão dōTERRA -> R$ ${formatarPreco(TAXA_ADESAO_BR, false)}\n`

  texto += `\n*Investimento:*\n`
  texto += `- Produtos: R$ ${formatarPreco(somaProdutos, false)}\n`
  if (precisaTaxa) texto += `- Taxa de adesão: R$ ${formatarPreco(TAXA_ADESAO_BR, false)}\n`
  texto += `- Frete: R$ ${formatarPreco(FRETE_BR, false)}\n`
  texto += `- *Total: R$ ${formatarPreco(total, false)}*\n`
  if (parcelamento) texto += `${parcelamento}\n`
  texto += `- Ou à vista no Pix / boleto`

  if (precisaTaxa) {
    texto += `\n\n*Sobre a taxa de adesão de R$ 48,00:*\n`
    texto += `O kit de adesão dōTERRA inclui uma seleção de óleos essenciais e garante 25% de desconto em todas as compras durante o ano. A taxa é isenta em compras acima de 100 PV.`
  } else {
    texto += `\n\n*🎉 Taxa de adesão isenta!*\n`
    texto += `Compra acima de 100 PV. Benefício: 25% de desconto em futuras compras durante o ano.`
  }

  return texto
}

function gerarOpcao3BR(lead: any): string {
  const oleosSet = new Set<string>()

  // Usar prioridades estruturadas (novo) ou fallback para texto livre
  const prioridades: string[] = lead?.prioridades || []
  if (prioridades.length > 0) {
    const itens = gerarProtocoloPrioritarioPorPrioridades(prioridades)
    itens.forEach((item: { oleo: string }) => {
      const nomeResolvido = resolverNomePrioritario(item.oleo)
      oleosSet.add(nomeResolvido)
    })
  } else {
    const protocoloPrioritario = gerarProtocoloPrioritario(lead)
    protocoloPrioritario.forEach(item => {
      if (item.oleo) {
        const nomeResolvido = resolverNomePrioritario(item.oleo)
        oleosSet.add(nomeResolvido)
      }
    })
  }

  const produtosLista: { info: ProdutoInfo }[] = []
  let somaProdutos = 0
  let totalPV = 0

  oleosSet.forEach(nomeResolvido => {
    const info = getProdutoInfo(nomeResolvido, false)
    if (info) {
      produtosLista.push({ info })
      somaProdutos += info.preco
      totalPV += info.pv
    }
  })

  somaProdutos += OLEO_COCO_PRECO_BR
  totalPV += OLEO_COCO_PV_BR

  const precisaTaxa = totalPV < PV_MINIMO_SEM_TAXA
  let total = somaProdutos + FRETE_BR
  if (precisaTaxa) total += TAXA_ADESAO_BR

  const parcelamento = calcularParcelamentoBR(total)

  let texto = `*OPÇÃO 3 — Protocolo Prioritário*\n\n`
  texto += `Óleos selecionados com base no que você mais quer resolver agora:\n\n`

  if (produtosLista.length > 0) {
    produtosLista.forEach(({ info }) => {
      texto += `- ${info.nome} ${info.tamanho} -> R$ ${formatarPreco(info.preco, false)}\n`
    })
  } else {
    texto += `(Nenhum óleo prioritário identificado)\n`
  }

  texto += `- Óleo de Coco Fracionado 115ml -> R$ ${formatarPreco(OLEO_COCO_PRECO_BR, false)} (essencial para diluição e uso tópico)\n`
  if (precisaTaxa) texto += `- Taxa de adesão dōTERRA -> R$ ${formatarPreco(TAXA_ADESAO_BR, false)}\n`

  texto += `\n*Investimento:*\n`
  texto += `- Produtos: R$ ${formatarPreco(somaProdutos, false)}\n`
  if (precisaTaxa) texto += `- Taxa de adesão: R$ ${formatarPreco(TAXA_ADESAO_BR, false)}\n`
  texto += `- Frete: R$ ${formatarPreco(FRETE_BR, false)}\n`
  texto += `- *Total: R$ ${formatarPreco(total, false)}*\n`
  if (parcelamento) texto += `${parcelamento}\n`
  texto += `- Ou à vista no Pix / boleto`

  if (precisaTaxa) {
    texto += `\n\n*Sobre a taxa de adesão de R$ 48,00:*\n`
    texto += `O kit de adesão dōTERRA inclui uma seleção de óleos essenciais e garante 25% de desconto em todas as compras durante o ano. A taxa é isenta em compras acima de 100 PV.`
  } else {
    texto += `\n\n*🎉 Taxa de adesão isenta!*\n`
    texto += `Compra acima de 100 PV. Benefício: 25% de desconto em futuras compras durante o ano.`
  }

  return texto
}

// ══════════════════════════════════════════
// GERACAO DAS OPCOES — PORTUGAL
// ══════════════════════════════════════════

function gerarOpcao1PT(lead: any): string {
  const oleosIndicados: string[] = (lead.oleos_indicados || []).map(resolverNomeOleo)

  // Separar óleos do kit que foram indicados vs não indicados
  const oleosKitIndicados: string[] = []
  const oleosKitNaoIndicados: string[] = []

  OLEOS_KIT_PT.forEach(nomeOleo => {
    if (oleosIndicados.includes(nomeOleo)) {
      oleosKitIndicados.push(nomeOleo)
    } else {
      oleosKitNaoIndicados.push(nomeOleo)
    }
  })

  // Óleos indicados que NÃO estão no kit (precisam ser comprados separadamente)
  const oleosForaDoKit: ProdutoInfo[] = []
  let somaOleosForaDoKit = 0

  OLEOS_ADICIONAIS_KIT_PT.forEach(nomeOleo => {
    if (oleosIndicados.includes(nomeOleo)) {
      const info = getProdutoInfo(nomeOleo, true)
      if (info) {
        oleosForaDoKit.push(info)
        somaOleosForaDoKit += info.preco
      }
    }
  })

  // Cálculo correto: Kit + óleos fora do kit + coco + frete
  const somaProdutos = KIT_PT_PRECO + somaOleosForaDoKit + OLEO_COCO_PRECO_PT
  const total = somaProdutos + FRETE_DPD

  const m = moeda(true)

  let texto = `*OPÇÃO 1 — Kit Essenciais Familiar (Melhor custo-benefício)*\n\n`
  texto += `O Kit Essenciais Familiar é a opção mais vantajosa para você. Ele inclui a maioria dos óleos essenciais indicados no seu diagnóstico e ainda outros óleos de uso diário que vão complementar o seu bem-estar. Adquirindo o kit, a taxa de inscrição dōTERRA é automaticamente isenta.\n\n`

  // 1. Óleos do kit que coincidem com o protocolo
  if (oleosKitIndicados.length > 0) {
    texto += `*Óleos do kit que coincidem com o seu protocolo:*\n`
    oleosKitIndicados.forEach(oleo => {
      const nomeExibicao = oleo === 'Mix Respiratório' ? 'Mix Respiratório (Air)' : oleo
      texto += `- ${nomeExibicao}\n`
    })
    texto += `\n`
  }

  // 2. Outros óleos incluídos no kit
  if (oleosKitNaoIndicados.length > 0) {
    texto += `*Outros óleos incluídos no kit:*\n`
    oleosKitNaoIndicados.forEach(oleo => {
      const nomeExibicao = oleo === 'Mix Respiratório' ? 'Mix Respiratório (Air)' : oleo
      texto += `- ${nomeExibicao}\n`
    })
    // Orégano está sempre no kit mas não está na tabela de diagnóstico
    texto += `- Orégano\n`
    texto += `\n`
  }

  // 3. Óleos do protocolo não incluídos no kit
  if (oleosForaDoKit.length > 0) {
    texto += `*Óleos do seu protocolo não incluídos no kit:*\n`
    oleosForaDoKit.forEach(info => {
      texto += `- ${info.nome} ${info.tamanho} \u2192 ${m}${formatarPreco(info.preco, true)}\n`
    })
    texto += `\n`
  }

  // Sempre incluir Óleo de Coco
  texto += `- Óleo de Coco Fracionado 115ml \u2192 ${m}${formatarPreco(OLEO_COCO_PRECO_PT, true)} (essencial para diluição e uso tópico)\n`

  // Investimento
  texto += `\n*Investimento:*\n`
  texto += `- Kit Essenciais Familiar: ${m}${formatarPreco(KIT_PT_PRECO, true)}\n`

  // Listar apenas os óleos adicionais não incluídos no kit
  oleosForaDoKit.forEach(info => {
    texto += `- ${info.nome} ${info.tamanho}: ${m}${formatarPreco(info.preco, true)}\n`
  })

  texto += `- Óleo de Coco Fracionado: ${m}${formatarPreco(OLEO_COCO_PRECO_PT, true)}\n`
  texto += `- Frete DPD: ${m}${formatarPreco(FRETE_DPD, true)} (ou DHL Express: ${m}${formatarPreco(FRETE_DHL, true)})\n`
  texto += `- Taxa de inscrição: isenta na compra do kit\n`
  texto += `- *Total: ${m}${formatarPreco(total, true)}*\n`
  texto += `- À vista`

  return texto
}

function gerarOpcao2PT(lead: any): string {
  const oleosIndicados: string[] = lead.oleos_indicados || []
  const produtosLista: { info: ProdutoInfo }[] = []
  let somaProdutos = 0
  let totalPV = 0

  const oleosJaAdicionados = new Set<string>()
  oleosIndicados.forEach((oleo: string) => {
    const nomeResolvido = resolverNomeOleo(oleo)
    if (oleosJaAdicionados.has(nomeResolvido)) return
    const info = getProdutoInfo(nomeResolvido, true)
    if (info) {
      oleosJaAdicionados.add(nomeResolvido)
      produtosLista.push({ info })
      somaProdutos += info.preco
      totalPV += info.pv
    }
  })

  somaProdutos += OLEO_COCO_PRECO_PT
  totalPV += OLEO_COCO_PV_PT

  const precisaTaxa = totalPV < PV_MINIMO_SEM_TAXA
  let total = somaProdutos + FRETE_DPD
  if (precisaTaxa) total += TAXA_INSCRICAO_PT

  const m = moeda(true)

  let texto = `*OPÇÃO 2 — Todos os Óleos do Seu Protocolo*\n*(Comprando avulso sem kit)*\n\n`
  produtosLista.forEach(({ info }) => {
    texto += `- ${info.nome} ${info.tamanho} -> ${m}${formatarPreco(info.preco, true)}\n`
  })
  texto += `- Óleo de Coco Fracionado 115ml -> ${m}${formatarPreco(OLEO_COCO_PRECO_PT, true)} (essencial para diluição e uso tópico)\n`
  if (precisaTaxa) texto += `- Taxa de inscrição dōTERRA -> ${m}${formatarPreco(TAXA_INSCRICAO_PT, true)}\n`

  texto += `\n*Investimento:*\n`
  texto += `- Produtos: ${m}${formatarPreco(somaProdutos, true)}\n`
  if (precisaTaxa) texto += `- Taxa de inscrição: ${m}${formatarPreco(TAXA_INSCRICAO_PT, true)}\n`
  texto += `- Frete DPD: ${m}${formatarPreco(FRETE_DPD, true)} (ou DHL Express: ${m}${formatarPreco(FRETE_DHL, true)})\n`
  texto += `- *Total: ${m}${formatarPreco(total, true)}*\n`
  texto += `- À vista`

  if (precisaTaxa) {
    texto += `\n\n*Sobre a taxa de inscrição de ${m}20,00:*\n`
    texto += `Esta taxa cobre o material de boas-vindas e o acesso ao escritório virtual dōTERRA. Se o seu pedido incluir um Kit de Inscrição, a taxa é automaticamente isenta.`
  }

  return texto
}

function gerarOpcao3PT(lead: any): string {
  const oleosSet = new Set<string>()

  const prioridades: string[] = lead?.prioridades || []
  if (prioridades.length > 0) {
    const itens = gerarProtocoloPrioritarioPorPrioridades(prioridades)
    itens.forEach((item: { oleo: string }) => {
      const nomeResolvido = resolverNomePrioritario(item.oleo)
      oleosSet.add(nomeResolvido)
    })
  } else {
    const protocoloPrioritario = gerarProtocoloPrioritario(lead)
    protocoloPrioritario.forEach(item => {
      if (item.oleo) {
        const nomeResolvido = resolverNomePrioritario(item.oleo)
        oleosSet.add(nomeResolvido)
      }
    })
  }

  const produtosLista: { info: ProdutoInfo }[] = []
  let somaProdutos = 0
  let totalPV = 0

  oleosSet.forEach(nomeResolvido => {
    const info = getProdutoInfo(nomeResolvido, true)
    if (info) {
      produtosLista.push({ info })
      somaProdutos += info.preco
      totalPV += info.pv
    }
  })

  somaProdutos += OLEO_COCO_PRECO_PT
  totalPV += OLEO_COCO_PV_PT

  const precisaTaxa = totalPV < PV_MINIMO_SEM_TAXA
  let total = somaProdutos + FRETE_DPD
  if (precisaTaxa) total += TAXA_INSCRICAO_PT

  const m = moeda(true)

  let texto = `*OPÇÃO 3 — Protocolo Prioritário*\n\n`
  texto += `Óleos selecionados com base no que você mais quer resolver agora:\n\n`

  if (produtosLista.length > 0) {
    produtosLista.forEach(({ info }) => {
      texto += `- ${info.nome} ${info.tamanho} -> ${m}${formatarPreco(info.preco, true)}\n`
    })
  } else {
    texto += `(Nenhum óleo prioritário identificado)\n`
  }

  texto += `- Óleo de Coco Fracionado 115ml -> ${m}${formatarPreco(OLEO_COCO_PRECO_PT, true)} (essencial para diluição e uso tópico)\n`
  if (precisaTaxa) texto += `- Taxa de inscrição dōTERRA -> ${m}${formatarPreco(TAXA_INSCRICAO_PT, true)}\n`

  texto += `\n*Investimento:*\n`
  texto += `- Produtos: ${m}${formatarPreco(somaProdutos, true)}\n`
  if (precisaTaxa) texto += `- Taxa de inscrição: ${m}${formatarPreco(TAXA_INSCRICAO_PT, true)}\n`
  texto += `- Frete DPD: ${m}${formatarPreco(FRETE_DPD, true)} (ou DHL Express: ${m}${formatarPreco(FRETE_DHL, true)})\n`
  texto += `- *Total: ${m}${formatarPreco(total, true)}*\n`
  texto += `- À vista`

  if (precisaTaxa) {
    texto += `\n\n*Sobre a taxa de inscrição de ${m}20,00:*\n`
    texto += `Esta taxa cobre o material de boas-vindas e o acesso ao escritório virtual dōTERRA. Se o seu pedido incluir um Kit de Inscrição, a taxa é automaticamente isenta.`
  }

  return texto
}

// ══════════════════════════════════════════
// OPCAO ESPECIAL — KIT ESSENCIAL PARA O LAR (Brasil)
// ══════════════════════════════════════════

function gerarOpcaoEspecialBR(): string {
  let texto = `\u2728 OPÇÃO ESPECIAL — Kit Essencial Para o Lar com Difusor\n\n`
  texto += `O kit completo dōTERRA para quem quer ter o melhor dos óleos essenciais em casa, em frascos maiores de 15ml e com difusor Pebble\u2122 incluso.\n\n`
  texto += `O kit inclui:\n`
  texto += `- Lavanda 15ml\n`
  texto += `- Limão Siciliano 15ml\n`
  texto += `- Hortelã-Pimenta 15ml\n`
  texto += `- Melaleuca 15ml\n`
  texto += `- Olíbano (Frankincense) 15ml\n`
  texto += `- Mix Respiratório (Breathe) 15ml\n`
  texto += `- Mix Digestivo (Zengest) 15ml\n`
  texto += `- Mix de Proteção (On Guard) 15ml\n`
  texto += `- Mix para Aterramento (Balance) 15ml\n`
  texto += `- Copaíba 15ml\n`
  texto += `- Tangerina 15ml\n`
  texto += `- Mix Calmante (Deep Blue) Rub\n`
  texto += `- Difusor Pebble\u2122\n`
  texto += `\n*Investimento:*\n`
  texto += `- Kit Essencial Para o Lar: R$1.888,00\n`
  texto += `- Frete: R$32,00\n`
  texto += `- *Total: R$1.920,00*\n`
  texto += `- Parcelado em 3x de R$640,00 no cartão\n`
  texto += `- Parcelado em 6x de R$320,00 no cartão\n`
  texto += `- Ou à vista no Pix / boleto\n`
  texto += `\n*Código do produto:* 60225644\n`
  texto += `*PV:* 200 pontos\n`
  texto += `*Taxa de adesão:* isenta (acima de 100 PV)`
  return texto
}

// ══════════════════════════════════════════
// COMPONENTE
// ══════════════════════════════════════════

export default function OpcoesCompra({ lead }: OpcoesCompraProps) {
  const [aberto, setAberto] = useState(true)

  const isPortugal = lead.pais === 'Portugal'

  const opcao1 = useMemo(() => isPortugal ? gerarOpcao1PT(lead) : gerarOpcao1BR(lead), [lead, isPortugal])
  const opcao2 = useMemo(() => isPortugal ? gerarOpcao2PT(lead) : gerarOpcao2BR(lead), [lead, isPortugal])
  const opcao3 = useMemo(() => isPortugal ? gerarOpcao3PT(lead) : gerarOpcao3BR(lead), [lead, isPortugal])
  const opcaoEspecial = useMemo(() => gerarOpcaoEspecialBR(), [])

  const copiarTexto = async (texto: string, label: string) => {
    try {
      await copiarParaArea(texto)
      toast.success(`${label} copiada!`)
    } catch {
      toast.error('Erro ao copiar')
    }
  }

  const abrirWhatsApp = (texto: string) => {
    const numero = formatarNumeroWhatsApp(lead.whatsapp)
    if (!numero) {
      toast.error('WhatsApp da cliente não disponível')
      return
    }
    const url = `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`
    window.open(url, '_blank')
  }

  const renderOpcao = (texto: string, corBorda: string, label: string) => (
    <div className={`bg-bege-fundo rounded-xl p-5 border-2 ${corBorda}`}>
      <div className="bg-white rounded-lg border border-bege-medio p-4 mb-4 max-h-72 overflow-y-auto">
        <pre className="text-sm text-texto-escuro whitespace-pre-wrap font-sans leading-relaxed">
          {texto}
        </pre>
      </div>
      <div className="flex gap-3">
        <button
          onClick={() => copiarTexto(texto, label)}
          className="flex-1 flex items-center justify-center gap-2 bg-verde-escuro text-white px-4 py-2.5 rounded-xl hover:bg-opacity-90 transition-all font-medium text-sm"
        >
          <Copy className="w-4 h-4" />
          Copiar opção
        </button>
        <button
          onClick={() => abrirWhatsApp(texto)}
          className="flex-1 flex items-center justify-center gap-2 text-white px-4 py-2.5 rounded-xl transition-all font-medium text-sm"
          style={{ backgroundColor: '#25D366' }}
        >
          <MessageCircle className="w-4 h-4" />
          Abrir no WhatsApp
        </button>
      </div>
    </div>
  )

  return (
    <div className="border-2 border-yellow-400 rounded-xl overflow-hidden">
      {/* Cabeçalho colapsável */}
      <button
        onClick={() => setAberto(!aberto)}
        className="w-full flex items-center justify-between px-6 py-4 bg-yellow-50 hover:bg-yellow-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <ShoppingCart className="w-6 h-6 text-yellow-600" />
          <h3 className="text-lg font-semibold text-yellow-800 font-cormorant">
            Opções de Compra {isPortugal ? '(Portugal)' : '(Brasil)'}
          </h3>
        </div>
        {aberto ? (
          <ChevronUp className="w-5 h-5 text-yellow-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-yellow-600" />
        )}
      </button>

      {aberto && (
        <div className="p-6 space-y-6 bg-white">
          {renderOpcao(opcao1, 'border-verde-claro', 'Opção 1')}
          {renderOpcao(opcao2, 'border-blue-300', 'Opção 2')}
          {renderOpcao(opcao3, 'border-yellow-300', 'Opção 3')}

          {/* ── OPÇÃO ESPECIAL — apenas Brasil ── */}
          {!isPortugal && (
            <div>
              {/* Caixa de aviso para a consultora */}
              <div
                className="flex items-start gap-3 p-4 rounded-xl mb-4"
                style={{ backgroundColor: '#FEF3C7', border: '1px solid #D97706' }}
              >
                <div
                  className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#D97706' }}
                >
                  <AlertTriangle className="w-4 h-4 text-white" />
                </div>
                <div className="text-sm leading-relaxed" style={{ color: '#92400E' }}>
                  <p className="font-semibold mb-1">⚠️ ATENÇÃO — QUANDO OFERECER ESSA OPÇÃO:</p>
                  <p className="mb-2">Essa opção é ideal para clientes com maior poder aquisitivo que buscam o melhor custo-benefício em frascos maiores, ou para empresas que querem implementar o bem-estar com óleos essenciais no ambiente de trabalho.</p>
                  <p className="mb-2">Observe durante o atendimento sinais como: cliente que menciona facilidade financeira, empresária, profissional liberal, ou que demonstra querer o protocolo mais completo possível.</p>
                  <p>Não ofereça essa opção como primeira escolha. Apresente sempre as Opções 1, 2 e 3 primeiro e só mencione essa opção especial se perceber abertura da cliente para um investimento maior.</p>
                </div>
              </div>

              {/* Bloco da Opção Especial */}
              <div
                className="rounded-xl p-5 border-2"
                style={{ background: 'linear-gradient(135deg, #FEF9C3 0%, #FDE68A 60%, #F9D155 100%)', borderColor: '#B45309' }}
              >
                {/* Título de destaque */}
                <div className="flex items-center gap-2 mb-4">
                  <div
                    className="flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0"
                    style={{ backgroundColor: '#B45309' }}
                  >
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#78350F' }}>Oferta Premium</p>
                    <h4 className="font-bold text-base leading-tight font-cormorant" style={{ color: '#451A03' }}>✨ OPÇÃO ESPECIAL — Kit Essencial Para o Lar com Difusor</h4>
                  </div>
                </div>

                {/* Texto da opção */}
                <div
                  className="rounded-lg border p-4 mb-4 max-h-72 overflow-y-auto"
                  style={{ backgroundColor: 'rgba(255,255,255,0.7)', borderColor: '#D97706' }}
                >
                  <pre className="text-sm text-texto-escuro whitespace-pre-wrap font-sans leading-relaxed">
                    {opcaoEspecial}
                  </pre>
                </div>

                {/* Botões */}
                <div className="flex gap-3">
                  <button
                    onClick={() => copiarTexto(opcaoEspecial, 'Opção especial')}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-all font-medium text-sm text-white"
                    style={{ backgroundColor: '#B45309' }}
                  >
                    <Copy className="w-4 h-4" />
                    Copiar opção especial
                  </button>
                  <button
                    onClick={() => abrirWhatsApp(opcaoEspecial)}
                    className="flex-1 flex items-center justify-center gap-2 text-white px-4 py-2.5 rounded-xl transition-all font-medium text-sm"
                    style={{ backgroundColor: '#25D366' }}
                  >
                    <MessageCircle className="w-4 h-4" />
                    Abrir no WhatsApp
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
